import { useCallback, useEffect, useState } from "react";
import { Loader2, MessageCircle, Send } from "lucide-react";
import { api, apiError, inr, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatementDialog } from "@/components/StatementDialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const BUCKETS = ["current", "1-30", "31-60", "61-90", "90+"];
const LABEL = { current: "Not due yet", "1-30": "1-30 days", "31-60": "31-60 days",
                "61-90": "61-90 days", "90+": "Over 90 days" };

export default function Collections() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [statement, setStatement] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/collections/dashboard");
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remindCustomer = async (row) => {
    setBusy(row.customer_id);
    try {
      const { data: overdue } = await api.get("/reminders/overdue");
      const mine = overdue.invoices.filter((i) => i.customer_id === row.customer_id);
      if (!mine.length) return toast.info("No overdue invoice for this customer right now");
      let sent = 0;
      for (const inv of mine) {
        try {
          const { data: res } = await api.post(`/reminders/invoice/${inv.invoice_id}/send`,
            { channel: "both" });
          if (res.delivered?.length) sent += 1;
          if (res.whatsapp?.wa_link && mine.length === 1)
            window.open(res.whatsapp.wa_link, "_blank", "noopener");
        } catch (e) {
          toast.info(apiError(e));
        }
      }
      toast.success(`${sent} reminder(s) sent to ${row.customer_name}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  return (
    <div>
      <PageHeader
        testId="collections-header"
        title="Collections"
        subtitle={`${inr(data.total_outstanding)} to collect • ${inr(data.overdue_total)} already overdue • ${data.counts.overdue_customers} customer(s) to chase`}
      />

      <div className="grid grid-cols-2 border-b border-border sm:grid-cols-5"
        data-testid="collections-ageing">
        {BUCKETS.map((b) => (
          <div key={b} className="kpi border-b border-r border-border">
            <div className="overline">{LABEL[b]}</div>
            <div className="mt-1.5 font-mono-num text-lg font-semibold">{inr(data.ageing[b])}</div>
          </div>
        ))}
      </div>

      <div className="space-y-6 p-4 sm:p-6">
        {data.chase_today.length > 0 && (
          <div>
            <h2 className="mb-2 text-base font-medium md:text-lg">Chase these first today</h2>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
              data-testid="chase-today-list">
              {data.chase_today.map((r) => (
                <div key={r.customer_id} className="surface p-3"
                  data-testid={`chase-card-${r.customer_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium">{r.customer_name}</div>
                    <Badge variant="destructive" className="text-[10px]">
                      {r.oldest_days}d
                    </Badge>
                  </div>
                  <div className="mt-1 font-mono-num text-lg font-semibold">
                    {inr(r.outstanding)}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {r.invoices} invoice(s) • oldest {r.oldest_invoice || "—"}
                    {r.last_payment_date && ` • last paid ${shortDate(r.last_payment_date)}`}
                  </div>
                  {r.over_credit_limit && (
                    <Badge variant="outline" className="mt-1 text-[10px]">
                      over credit limit
                    </Badge>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {can("payments", "edit") && (
                      <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]"
                        disabled={busy === r.customer_id}
                        data-testid={`chase-remind-${r.customer_id}`}
                        onClick={() => remindCustomer(r)}>
                        <MessageCircle className="h-3 w-3" /> Remind
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]"
                      data-testid={`chase-statement-${r.customer_id}`}
                      onClick={() => setStatement({ id: r.customer_id, name: r.customer_name })}>
                      <Send className="h-3 w-3" /> Statement
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h2 className="mb-2 text-base font-medium md:text-lg">Everyone who owes money</h2>
          {data.customers.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground"
              data-testid="collections-empty">
              Nothing outstanding — every invoice is settled.
            </p>
          ) : (
            <div className="surface overflow-x-auto" data-testid="collections-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                    <TableHead className="text-right">Overdue</TableHead>
                    <TableHead>Oldest</TableHead>
                    <TableHead>Last payment</TableHead>
                    <TableHead>Last reminder</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.customers.map((r) => (
                    <TableRow key={r.customer_id} data-testid={`collection-row-${r.customer_id}`}>
                      <TableCell className="text-sm">
                        {r.customer_name}
                        <div className="text-[11px] text-muted-foreground">
                          {r.invoices} invoice(s)
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono-num text-sm">
                        {inr(r.outstanding)}
                      </TableCell>
                      <TableCell className="text-right font-mono-num text-sm">
                        {inr(r.overdue_amount)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.oldest_days > 0
                          ? <>{r.oldest_days}d <span className="text-muted-foreground">{r.oldest_invoice}</span></>
                          : "not due"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.last_payment_date
                          ? `${shortDate(r.last_payment_date)} • ${inr(r.last_payment_amount)}`
                          : "none"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.last_reminder_at ? shortDate(r.last_reminder_at) : "never"}
                      </TableCell>
                      <TableCell className="space-x-1 text-right">
                        <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                          data-testid={`collection-statement-${r.customer_id}`}
                          onClick={() => setStatement({ id: r.customer_id, name: r.customer_name })}>
                          Statement
                        </Button>
                        {can("payments", "edit") && r.overdue_amount > 0 && (
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                            disabled={busy === r.customer_id}
                            data-testid={`collection-remind-${r.customer_id}`}
                            onClick={() => remindCustomer(r)}>
                            Remind
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <StatementDialog customer={statement} onClose={() => setStatement(null)} />
    </div>
  );
}
